import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { LinkedMoiOverviewComponent } from "@app/moi/moi-linked-overview/moi-linked-overview.component";
import { FormValidationHelper, ValidationMessage } from "@app/shared/helpers/form-validation-helper";
import { HistoryOverviewComponent } from "@app/shared/history-overview/history-overview.component";
import { MoiTypes } from "@enums/moi/MoiTypes";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ThirdPartyModel } from "@models/thirdparty/ThirdpartyModel";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { ThirdPartyDataService } from "../services/ThirdPartyDataService";
import { ThirdPartyControlsComponent } from "../thirdparty-controls/thirdparty-controls.component";
import { ThirdPartyRisksComponent } from "../thirdparty-risks/thirdparty-risks.component";
import { ThirdPartyProcessingPurposeComponent } from "./thirdparty-processingpurpose/thirdparty-processingpurpose.component";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { ThirdPartyPermissions } from "@models/thirdparty/ThirdPartyPermissions";
import { ThirdPartyContractsComponent } from "../thirdparty-contracts/thirdparty-contracts.component";
import { ThirdPartyContractsOverviewComponent } from "../thirdparty-contracts-overview/thirdparty-contracts-overview.component";
import { TargetModule } from "@enums/document/TargetModule";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { ThirdPartyConfigModel } from "@models/thirdparty/third-party-config-model";
import { TabComponentHelper } from "@app/shared/helpers/tab-component-helper";
import { isGuid } from "@methods/uniqueMethods";
import { ThirdPartyLinkedFormsComponent } from "../thirdparty-forms/thirdparty-forms.component";
import { ThirdPartyIncidentsComponent } from "../thirdparty-incidents/thirdparty-incidents.component";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { firstValueFrom } from "rxjs";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { ThirdPartyDetailMainComponent } from "./uc/thirdparty-detail-main.component";
import { DocumentManagerOldComponent } from "@app/shared/document-manager-old/document-manager-old.component";
import { HyperlinkManagerComponent } from "@app/shared/hyperlink-manager/hyperlink-manager.component";
import { DetailOptionsComponent } from "@app/shared/components/detail-options/detail-options.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/thirdparty/i18n/scope";
import { scope as rootScope } from "@app/shared/i18n/scope";

@Component({
    selector: "thirdparty-detail",
    templateUrl: "./thirdparty-detail.component.html",
    styleUrls: ["./thirdparty-detail.component.scss"],
    imports: [
        MenuSystemDirective,
        ThirdPartyDetailMainComponent,
        ThirdPartyContractsOverviewComponent,
        ThirdPartyRisksComponent,
        ThirdPartyControlsComponent,
        LinkedMoiOverviewComponent,
        ThirdPartyIncidentsComponent,
        ThirdPartyLinkedFormsComponent,
        DocumentManagerOldComponent,
        HyperlinkManagerComponent,
        HistoryOverviewComponent,
        DetailOptionsComponent,
        DateTimeFormatPipe,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class ThirdPartyDetailComponent implements OnInit {
    private _toastr = inject(ToastrService);
    dataservice = inject(ThirdPartyDataService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _settingsDS = inject(SettingsDataService);
    private _translation = injectTranslation(scope);
    private _rootTranslation = injectTranslation(rootScope);

    @ViewChild(HistoryOverviewComponent) historyOverviewComponent;
    @ViewChild("thirdpartyRisksComponent")
    thirdpartyRisksComponent: ThirdPartyRisksComponent;
    @ViewChild("thirdpartyControlsComponent")
    thirdpartyControlsComponent: ThirdPartyControlsComponent;
    @ViewChild("thirdpartyMoisComponent")
    thirdPartyMoisComponent: LinkedMoiOverviewComponent;
    @ViewChild("thirdpartyProcessingPurposeComponent")
    ThirdPartyProcessingPurposeComponent: ThirdPartyProcessingPurposeComponent;
    @ViewChild("thirdpartyContractsComponent")
    ThirdPartyContractsComponent: ThirdPartyContractsComponent;
    @ViewChild("thirdpartyContractsOverviewComponent")
    thirdpartyContractsOverviewComponent: ThirdPartyContractsOverviewComponent;
    @ViewChild("thirdPartyFormsComponent")
    thirdPartyFormsComponent: ThirdPartyLinkedFormsComponent;
    @ViewChild("thirdpartyIncidentsComponent")
    thirdpartyIncidentsComponent: ThirdPartyIncidentsComponent;

    thirdParty: ThirdPartyModel;

    tpMoiType = MoiTypes.TP_Moi;
    tab: TabModel;
    tabID: string;
    id: string;

    sd: any;

    parentForm: FormGroup;

    documentsReady = false;
    readonly: boolean;

    targetModule = TargetModule.ThirdParty;
    permissions: ThirdPartyPermissions = new ThirdPartyPermissions();

    hyperlinksMenuItemId = "menuItemHyperlinks";
    useIncidents: boolean = false;

    async ngOnInit() {
        // The toasts, tab name and validation messages below are translated before the
        // template renders.
        await Promise.all([this._translation.ready(), this._rootTranslation.ready()]);

        this.id = this.tab.id;

        this.useIncidents = (
            await firstValueFrom(this._settingsDS.getSetting(ApplicationSettings.UseIncidents))
        ).BoolValue;

        const standingDataRequest = firstValueFrom(this.dataservice.getStandingData());
        this.sd = await standingDataRequest;
        if (!this.sd) {
            this._toastr.error(this._translation.t("detail.somethingWentWrong"));
            this.tab.close();
        }
        this.setupThirdParty();
    }

    InitThirdparty() {
        if (!this.thirdParty.ID || this.thirdParty.ID <= 0) {
            this.initNewThirdPartyModel();
        }

        this.initForms(this.thirdParty);

        if (!this.thirdParty.Documents) {
            this.thirdParty.Documents = [];
            this.documentsReady = true;
        }
        if (!this.thirdParty.LinkedRisks) {
            this.thirdParty.LinkedRisks = [];
        }
        if (!this.thirdParty.Contacts) {
            this.thirdParty.Contacts = [];
        }
        this.tab.name = this.thirdParty.Guid
            ? `(TP) ${this.thirdParty.Identifier} - ${this.thirdParty.Name}`
            : this._translation.t("detail.addNewThirdParty");
        this.tab.showLoader = false;
    }

    initNewThirdPartyModel() {
        const conf = <ThirdPartyConfigModel>this.tab.config;
        if (conf) {
            const orgID = conf.organizationId;
            if (orgID) {
                this.thirdParty.Organizations = [+orgID];
            }

            const bdID = conf.businessId;
            if (bdID) {
                this.thirdParty.BusinessDimensions = [+bdID];
            }
        }
    }

    handleMenuItemClick(menuItem: TabMenuItem) {
        if (menuItem) {
            switch (menuItem.menuItemId) {
                case "history": {
                    this.historyOverviewComponent.loadHistory();
                    break;
                }
                case "risks": {
                    this.thirdpartyRisksComponent.load();
                    break;
                }
                case "controls": {
                    this.thirdpartyControlsComponent.load();
                    break;
                }
                case "moi": {
                    this.thirdPartyMoisComponent.load(false);
                    break;
                }
                case "incidents": {
                    this.thirdpartyIncidentsComponent.load();
                    break;
                }
                case "contracts": {
                    this.thirdpartyContractsOverviewComponent.load();
                    break;
                }
                case "forms": {
                    this.thirdPartyFormsComponent.load();
                    break;
                }
                case this.hyperlinksMenuItemId:
                    this.loadHyperlinks();
                    break;
            }
        }
    }

    async save() {
        await Promise.all([this._translation.ready(), this._rootTranslation.ready()]);

        if (this.permissions.canEdit && !this.parentForm.valid) {
            FormValidationHelper.markAllAsTouched(this.parentForm);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.parentForm);
            const validationMessage = FormValidationHelper.getGeneralErrorMessage(
                validationErrors,
                (key, params) => this._rootTranslation.t(key, params),
            );
            this._toastr.warning(validationMessage, this._translation.t("detail.saveFailed"), {
                enableHtml: true,
            });
            return;
        }

        FormValidationHelper.mapToModel(this.parentForm, this.thirdParty);
        this.thirdParty.Country =
            !this.thirdParty.Country || this.thirdParty.Country.trim() === ""
                ? null
                : this.thirdParty.Country;

        const savingPrompt = this._promptService.loader(
            this._translation.t("detail.savingChanges"),
        );
        const call = firstValueFrom(this.dataservice
            .storeThirdParty(this.thirdParty)
            .pipe(finalize(() => savingPrompt.close())))
            .then((guid) => {
                this.tab.id = guid;
            });

        await call;

        this._tabService.refresh(this._pages.Thirdparty);

        this._toastr.success(this._translation.t("detail.updateCompleted"));
        this.tab.refresh();
    }

    delete() {
        this.dataservice.deleteThirdParty(this.thirdParty.Guid).subscribe(
            (validations) => {
                if (validations && validations.length > 0) {
                    this._toastr.warning(validations.toString());
                } else {
                    this._toastr.success(this._translation.t("detail.thirdPartyDeleted"));
                    this._tabService.refresh(this._pages.Thirdparty);
                    this.tab.close(false);
                }
            },
            (error) => {
                this._toastr.error("", error || error.Message);
            },
        );
    }

    setupThirdParty() {
        this.dataservice.getPermissions(this.id).subscribe(
            (permissions: ThirdPartyPermissions) => {
                this.permissions = permissions;
                if (!this.permissions.canView) {
                    this._toastr.error(this._translation.t("detail.notEnoughRights"));
                    this.tab.close(false);
                }
                this.dataservice.getThirdParty(this.id).subscribe((thirdParty: ThirdPartyModel) => {
                    this.thirdParty = thirdParty;
                    if (this.thirdParty.Country === "") {
                        this.thirdParty.Country = null;
                    }
                    this.InitThirdparty();
                });
            },
            () => {
                this.tab.close(false);
            },
        );
    }

    initForms(thirdparty: ThirdPartyModel) {
        this.parentForm = new FormGroup(
            {
                Name: new FormControl(thirdparty.Name, [Validators.required]),
                Address: new FormControl(thirdparty.Address),
                Owner: new FormControl(thirdparty.Owner),
                ThirdpartyResponsible: new FormControl(thirdparty.ThirdpartyResponsible),
                Description: new FormControl(thirdparty.Description),
                EUMember: new FormControl(thirdparty.EUMember),
                CocNumber: new FormControl(thirdparty.CocNumber, [
                    Validators.min(0),
                    Validators.max(2147483647),
                ]),
                TaxNumber: new FormControl(thirdparty.TaxNumber),
                Country: new FormControl(thirdparty.Country),
                City: new FormControl(thirdparty.City),
                BeneficialOwner: new FormControl(thirdparty.BeneficialOwner),
                AuthorizedSignatory: new FormControl(thirdparty.AuthorizedSignatory),
                StatusOfInvolvementID: new FormControl(thirdparty.StatusOfInvolvementID),
                InitialReviewDate: new FormControl(thirdparty.InitialReviewDate),
                NextReviewDate: new FormControl({
                    value: thirdparty.NextReviewDate,
                    disabled: true,
                }),
                ReviewFrequency: new FormControl(thirdparty.ReviewFrequency),
                Reviewers: new FormControl(thirdparty.Reviewers),
                UserResponsibles: new FormControl(thirdparty.UserResponsibles),
                Phone: new FormControl(
                    thirdparty.Phone,
                    FormValidationHelper.validatePhoneControl(
                        this._translation.t("detailMain.phoneLabel"),
                    ),
                ),
                Email: new FormControl(
                    thirdparty.Email,
                    FormValidationHelper.validateEmailControl(
                        this._translation.t("detailMain.emailLabel"),
                    ),
                ),
                Website: new FormControl(
                    thirdparty.Website,
                    FormValidationHelper.validateWebsiteControl(
                        this._translation.t("detailMain.websiteLabel"),
                    ),
                ),
                ContractValue: new FormControl(thirdparty.ContractValue, Validators.min(0)),
                BusinessDimensions: new FormControl(thirdparty.BusinessDimensions),
                FrameworkDimensions: new FormControl(thirdparty.FrameworkDimensions),
                CurrencyCode: new FormControl(thirdparty.CurrencyCode),
                Organizations: new FormControl(thirdparty.Organizations, Validators.required),
                LinkedServiceIds: new FormControl(thirdparty.LinkedServiceIds),
                Comment: new FormControl(thirdparty.Comment),
                DataController: new FormControl(thirdparty.DataController),
                DataRecipient: new FormControl(thirdparty.DataRecipient),
                DataProcessor: new FormControl(thirdparty.DataProcessor),
                ThirdParty: new FormControl(thirdparty.ThirdParty),
                LeiCode: new FormControl(thirdparty.LeiCode, [
                    FormValidationHelper.validateFixedLength(
                        this._translation.t("detail.leiCodeField"),
                        20,
                    ),
                ]),
                DUNS_Number: new FormControl(
                    thirdparty.DUNS_Number,
                    FormValidationHelper.validateMax(
                        this._translation.t("detailMain.dunsNumberLabel"),
                        1000000000,
                    ),
                ), // max 9 digits
                OrganizationBeneficialOwner: new FormControl(
                    thirdparty.OrganizationBeneficialOwner,
                ),
                OrganizationResponsibleId: new FormControl(
                    thirdparty.OrganizationResponsibleId,
                    Validators.required,
                ),
                Relationship: new FormControl(thirdparty.Relationship),
                Criticality: new FormControl(thirdparty.Criticality),
                StartDateServicing: new FormControl(thirdparty.StartDateServicing),
                EndDateServicing: new FormControl(thirdparty.EndDateServicing),
            },
            {
                validators: [
                    FormValidationHelper.validateStartEndDate<ThirdPartyModel>(
                        "StartDateServicing",
                        "EndDateServicing",
                        new ValidationMessage("validation.endBeforeStart", {
                            end: this._translation.t("detailMain.endDateServicingLabel"),
                            start: this._translation.t("detailMain.startDateServicingLabel"),
                        }),
                        false,
                    ),
                ],
            },
        );

        if (!this.permissions.canEdit) {
            this.parentForm.disable();
        }
    }

    loadHyperlinks(): void {
        if (!this.thirdParty.Hyperlinks && this.id && isGuid(this.id.toString())) {
            this.dataservice.getHyperlinks(this.id.toString()).subscribe((hyperlinks) => {
                this.thirdParty.Hyperlinks = hyperlinks;
            });
        }
    }

    checkHyperlinksDirty(): void {
        TabComponentHelper.toggleTabDirty(this.tab, this.hyperlinksMenuItemId, true);
    }
}
